import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/Button.js';
import { type PricingRule, emptyRule } from '../types.js';
import { PricingRuleDrawer } from './PricingRuleDrawer.js';
import { summarizeAction, summarizeWhen } from './ruleSummary.js';

interface Props {
  rules: PricingRule[];
  currencySymbol: string;
  onChange: (rules: PricingRule[]) => void;
}

/** Reassign sequential `order` (1-based) following array position. */
function reindex(rules: PricingRule[]): PricingRule[] {
  return rules.map((r, i) => ({ ...r, order: i + 1 }));
}

export function PricingRulesEditor({ rules, currencySymbol, onChange }: Props) {
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  function openAdd() {
    setEditIndex(null);
    setDrawerOpen(true);
  }
  function openEdit(index: number) {
    setEditIndex(index);
    setDrawerOpen(true);
  }
  function move(index: number, dir: -1 | 1) {
    const target = index + dir;
    if (target < 0 || target >= rules.length) return;
    const next = [...rules];
    const a = next[index];
    const b = next[target];
    if (!a || !b) return;
    next[index] = b;
    next[target] = a;
    onChange(reindex(next));
  }
  function toggle(index: number) {
    const next = rules.map((r, i) => (i === index ? { ...r, enabled: !r.enabled } : r));
    onChange(next);
  }
  function remove(index: number) {
    onChange(reindex(rules.filter((_, i) => i !== index)));
  }
  function save(rule: PricingRule) {
    if (editIndex === null) {
      onChange(reindex([...rules, rule]));
    } else {
      onChange(rules.map((r, i) => (i === editIndex ? rule : r)));
    }
    setDrawerOpen(false);
    setEditIndex(null);
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Pricing rules</h2>
        <Button type="button" variant="ghost" onClick={openAdd}>
          <Plus className="mr-1 h-4 w-4" /> Add rule
        </Button>
      </div>

      {rules.length === 0 ? (
        <p className="rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline bg-surface px-4 py-6 text-center text-sm text-steel">
          No pricing rules yet. The base rates apply on their own.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl shadow-[0px_1px_2px_0px_rgba(0,30,43,0.04)] border border-hairline">
          <table className="w-full text-sm">
            <thead className="bg-surface text-left text-xs uppercase tracking-wide text-steel">
              <tr>
                <th className="px-2 py-2 w-16">Order</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-2 py-2">On</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, i) => (
                <tr key={`${rule.order}-${i}`} className="border-t border-hairline text-ink">
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        aria-label="Move up"
                        disabled={i === 0}
                        className="p-2 -m-2 text-steel hover:text-ink disabled:opacity-30"
                        onClick={() => move(i, -1)}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Move down"
                        disabled={i === rules.length - 1}
                        className="p-2 -m-2 text-steel hover:text-ink disabled:opacity-30"
                        onClick={() => move(i, 1)}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {rule.name || <span className="text-steel">—</span>}
                  </td>
                  <td className="px-3 py-2 text-slate">{summarizeWhen(rule)}</td>
                  <td className="px-3 py-2 text-slate">{summarizeAction(rule, currencySymbol)}</td>
                  <td className="px-2 py-2">
                    <input type="checkbox" checked={rule.enabled} onChange={() => toggle(i)} />
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        aria-label="Edit rule"
                        className="p-2 -m-2 text-steel hover:text-ink"
                        onClick={() => openEdit(i)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        aria-label="Remove rule"
                        className="p-2 -m-2 text-steel hover:text-red-600"
                        onClick={() => remove(i)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {drawerOpen && (
        <PricingRuleDrawer
          open={drawerOpen}
          initial={
            editIndex === null ? emptyRule(rules.length + 1) : (rules[editIndex] ?? emptyRule(1))
          }
          currencySymbol={currencySymbol}
          onCancel={() => {
            setDrawerOpen(false);
            setEditIndex(null);
          }}
          onSave={save}
        />
      )}
    </div>
  );
}
